import FroalaEditor from 'react-froala-wysiwyg';
import config from '../../config';

import "froala-editor/js/plugins/align.min.js";
import "froala-editor/js/plugins/font_size.min.js";
import "froala-editor/js/plugins/font_family.min.js";
import "froala-editor/js/plugins/colors.min.js";
import "froala-editor/js/plugins/link.min.js";
import "froala-editor/js/plugins/lists.min.js";
import "froala-editor/js/plugins/paragraph_format.min.js";
import "froala-editor/js/plugins/line_height.min.js";

import 'froala-editor/css/froala_style.min.css';
import 'froala-editor/css/froala_editor.pkgd.min.css';
import {useEffect, useMemo, useRef } from "react";
import './rich-text-editor.scss';
import useInputValidation from "../../hooks/input-validation-hook";
import InputErrorMessages from '../inputs/input-error-messages/input-error-messages';
import { IValidationProps } from '../inputs/input/input';

interface IPatchBehavior {
    event?: string;
}

export enum EDITOR_VARIANT {
    REGULAR = 'REGULAR',
    BULLETS_ONLY = 'BULLETS_ONLY',
    EMAIL_EDITOR_ONLY = 'EMAIL_EDITOR_ONLY',
    HYPERLINK_ONLY = 'HYPERLINK ONLY',
}
const RETURNGO_PREDEFINED_VARIABLES = [
    "rma_id",
    "payment_authorization_expiration_date",
    "rma_created_at",
    "rma_status",
    "order_name",
    "first_name",
    "last_name",
    "full_name",
    "store_name",
    "store_url",
    "reject_reason",
    "cancel_reason",
    "expire",
    "credits",
    "payment_currency_code",
    "customer_support_url",
    "carrier_location_name",
    "refund_provider",
    "tracking_number",
    "tracking_link",
    "expected_delivery_date",
    "shipping_date",
    "return_label_url",
    "packing_slip_url",
    "qr_code_link",
    "return_method",
    "return_type",
    "order_tracking_number",
    "portal_tracking_url",
    "unsubscribe_link",
    "status_description",
    "estimated_delivery_date",
    "carrier_name"
];

const TOOLBAR_VARS = {
    [EDITOR_VARIANT.REGULAR]: [
        "bold",
        "italic",
        "underline",
        "fontFamily",
        "fontSize",
        "textColor",
        "backgroundColor",
        "paragraphFormat",
        "lineHeight",
        "align",
        "formatOL",
        "formatUL",
        "outdent",
        "indent",
        "leftToRight",
        "rightToLeft",
        "insertLink",
        "clearFormatting",
        "selectAll",
        "undo",
        "redo",
    ],
    [EDITOR_VARIANT.BULLETS_ONLY]: [
        "bold",
        "italic",
        "underline",
        "fontFamily",
        "textColor",
        "backgroundColor",
        "align",
        "formatUL",
        "outdent",
        "indent",
        "leftToRight",
        "rightToLeft",
        "insertLink",
        "clearFormatting",
        "selectAll",
        "undo",
        "redo",
    ],
    [EDITOR_VARIANT.HYPERLINK_ONLY]: [
        "bold",
        "italic",
        "underline",
        "textColor",
        "fontSize",
        "backgroundColor",
        "insertLink",
        "clearFormatting",
        "selectAll",
        "undo",
        "redo",
    ],
    [EDITOR_VARIANT.EMAIL_EDITOR_ONLY]: [
        "bold",
        "italic",
        "underline",
        "fontFamily",
        "textColor",
        "backgroundColor",
        "align",
        "formatUL",
        "leftToRight",
        "rightToLeft",
        "insertLink",
        "clearFormatting",
        "selectAll",
        "undo",
        "redo",
    ],
};

interface IProps extends IValidationProps {
    value?: string | null,
    onChange: (value: string) => void,
    placeholder?: string,
    disabled?: boolean,
    minHeight?: number,
    maxHeight?: number,
    variant?: EDITOR_VARIANT,
    linkAlwaysBlank?: boolean;
    useVariableSpecialPaste?: boolean;
    pastePlain?: boolean;
}

export const RichTextEditor: React.FC<IProps> = ({ value, onChange, placeholder, disabled, minHeight, maxHeight, variant = EDITOR_VARIANT.REGULAR, linkAlwaysBlank = true, useVariableSpecialPaste = false, validationGroup, required, validationCallback, showValidationMessage, pastePlain = false }) => {
    /** Rich text editor is breaking if the initial value is undefined or null, since that means it doesn't have a model to edit. 
     * therefore we need to set an empty string as the initial value if it's undefined or null. */
    const safeValue = isEmptyString(value) ? '' : value!;
    const editorRef = useRef<any>();
    const initialValue = useRef<string>(safeValue);
    
    const { isValid, messages } = useInputValidation({ value:safeValue, validation: { validationGroup, required, validationCallback, showValidationMessage } });
    const toolbarButtons = () => {
        return TOOLBAR_VARS[variant ?? EDITOR_VARIANT.REGULAR] ?? TOOLBAR_VARS[EDITOR_VARIANT.REGULAR];
    }

    /** froala editor changes the original string at load (replaces html tags like b to strong etc...)
     * this is an attempt to prevent showing those changes if there was no user input.
     * skipping calling on change handler if the content changed is called right after html.set event */
    const patchBehaviorObj = useMemo(() => ({}), []) as IPatchBehavior;

    const handleOnChange = (value: string) => {
        if (patchBehaviorObj.event === 'html.set') {
            patchBehaviorObj.event = undefined;
            return;
        }
        onChange(value);
    }
    
    const isSpecialVariablePasteEnabled = () => {
        return useVariableSpecialPaste;
    }

    const handleInitialBulletsMode = () => {
        if (variant !== EDITOR_VARIANT.BULLETS_ONLY) {
            return;
        }
        if (editorRef.current && isEmptyString(initialValue.current)) {
            /** preselects bullets only when initial value is empty */
            editorRef.current.commands.selectAll();
            editorRef.current.lists.format('UL');
            /** after the above changes the input captures focus - this is attempt to loose the focus */
            editorRef.current.edit.off();
            editorRef.current.edit.on();
        }
    }

    useEffect(() => {
        if (editorRef.current) {
            if (disabled) {
                (editorRef.current as any).edit.off();
            }
            else {
                (editorRef.current as any).edit.on();
            }
        }
    }, [disabled])
    
    
    useEffect(()=>{
        const editorEl = editorRef.current?.el;
        if (!editorEl) {
            return;
        }
        const el = editorEl.closest('.froala-editor');
        if (!el) {
            return;
        }
        if (isValid) {
            el.classList.remove('invalid');
        } else {
            el.classList.add('invalid');
        }
    },[isValid])
    
    return (
        <>
        <FroalaEditor
            tag='textarea'
            config={{
                key: config.froalaKey,
                attribution: false,
                toolbarSticky: false,
                quickInsertEnabled: false,
                placeholderText: placeholder,
                heightMin: minHeight,
                heightMax: maxHeight,
                toolbarButtons: toolbarButtons(),
                editorClass:"froala-editor" + (!isValid ? ' invalid' : ''),
                htmlUntouched: true,
                linkAlwaysBlank: linkAlwaysBlank,
                pastePlain: pastePlain,
                events: {
                    'html.set': function () {
                        /** when the value initialy is empty this event is not followed by onChange, hence the special case */
                        if (!isEmptyString(safeValue)) {
                            patchBehaviorObj.event = 'html.set';
                        }
                        // console.log('html.set', '*' + value + '*', this);
                    },
                    'initialized': function () {
                        editorRef.current = this;
                        /** set initial disabled value */
                        if (disabled) {
                            (editorRef.current as any).edit.off();
                        }
                        handleInitialBulletsMode();
                    },
                    'focus': function () {
                    },
                    'paste.afterCleanup': function (clipboard_html: string) {
                        if (isSpecialVariablePasteEnabled()) {
                            const tempElement = document.createElement('div');
                            tempElement.innerHTML = clipboard_html;
                            let textContent = tempElement.textContent as string;
                            tempElement.remove();
                            
                            textContent = textContent.replace(/<[^>]*>/g, '');
                            textContent = textContent.replace(/['"]/g, '');
                            textContent = textContent.replace(/[{}]/g, '');
                            textContent = textContent.trim();
                            
                            if (RETURNGO_PREDEFINED_VARIABLES.includes(textContent.toLowerCase())) {
                                return `{{${textContent}}}`;
                            }
                        }
                        
                        return clipboard_html;
                    }
                }
            }}
            model={safeValue}
            onModelChange={handleOnChange}
        />
        <InputErrorMessages messages={messages} />
        </>
    )
}

function isEmptyString(s?: string | null): boolean {
    return s === undefined || s === null || s.trim() === '';
}
